import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { environment } from 'src/environments/environment';
import { t } from 'src/app/shared/_helpers/translation.helper';
import {
  UserManagementService,
  TableNames,
} from '../../services/user-management.service';
import { TenantService, TenantNameDto } from '../../../../_services/tenant.service';
import { AdminTenantDto } from '../../../../_models/admin-tenant-dto';
import {
  buildTenantFilterOptions,
  getAdminTenantLabel,
  tenantLabelLookupFromMap,
} from '../../../../shared/_helpers/tenant-label.helper';
import { UserManagementDTO, UserStatus, UserType } from '../../models/UserManagementDTO';
import { AuthService } from '../../../../_services/auth.service';
import { TokenService } from '../../../../_services/token.service';
import { FEATURE_ROLES, ADMIN_ROLE, FULL_ACCESS_ENG, FULL_ACCESS_GER, NO_PERMISSION_ENG, NO_PERMISSION_GER } from '../../../../_helpers/Constants';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  ColDef,
  GridApi,
  GetContextMenuItemsParams,
  MenuItemDef,
} from 'ag-grid-community';
import { GenericMultiSelectFilterComponent } from 'src/app/shared/ag-grid-components/generic-multi-select-filter/generic-multi-select-filter.component';
import { SearchFilterComponent } from 'src/app/shared/ag-grid-components/search-filter/search-filter.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subject, debounceTime, distinctUntilChanged, tap, switchMap, map, filter } from 'rxjs';
import { AgGridTableComponent } from 'src/app/shared/components/ag-grid-table/ag-grid-table.component';

@Component({
  selector: 'app-user-managment-page',
  templateUrl: './user-management-page.component.html',
  styleUrls: ['./user-management-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserManagementPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('templateResetPassword') templateResetPassword!: TemplateRef<any>;
  @ViewChild('editUserTemplate') editUserTemplate!: TemplateRef<any>;
  @ViewChild('templateDeleteUser') templateDeleteUser!: TemplateRef<any>;
  @ViewChild(AgGridTableComponent) agGridTable!: AgGridTableComponent;
  @ViewChild('stickyToolbar', { read: ElementRef }) stickyToolbar!: ElementRef<HTMLElement>;
  @ViewChild('scrollSentinel') scrollSentinel!: ElementRef;

  private dropdownListener?: (event: MouseEvent) => void;
  private toolbarResizeObserver?: ResizeObserver;
  private scrollObserver?: IntersectionObserver;
  isLoading = true;
  loadingMore = false;
  hasMoreData = false;
  searchField = '';
  modalRef?: BsModalRef;

  allUsers: UserManagementDTO[] = [];
  private filteredUsers: UserManagementDTO[] = [];
  displayedUsers: UserManagementDTO[] = [];
  private currentPage = 1;
  private readonly pageSize = 50;

  search = new Subject<string>();

  public onClose: Subject<boolean> = new Subject();
  tenants: AdminTenantDto[] = [];
  tenantNames: TenantNameDto[] = [];
  // `Tenant/names` already sends the display name in `name`
  protected get tenantOptions() {
    return this.tenantNames.map(t => ({ value: t.id, label: t.name }));
  }
  selectedUser: UserManagementDTO = {
    email: '',
    firstName: '',
    lastName: '',
    roles: [],
    userId: '',
    userTenants: [],
  };
  isEmailExist = false;
  isEmailChecking = false;
  isCreatingUser = false;
  userId: any;
  modalForm!: FormGroup;
  createUserForm!: FormGroup;

  // AG Grid properties
  private gridApi!: GridApi;
  private currentTooltip: HTMLElement | null = null;
  localeText = { noRowsToShow: '' };
  suppressRowClickSelection = true;

  // Context menu configuration
  getContextMenuItems = (_params: GetContextMenuItemsParams) => {
    const result: (string | MenuItemDef)[] = [
      {
        name: this.tr('exportToExcel'),
        icon: '<i class="fa fa-file-excel-o"></i>',
        action: () => this.exportAllData('excel'),
      } as MenuItemDef,
      {
        name: this.tr('exportToCsv'),
        icon: '<i class="fa fa-file-text-o"></i>',
        action: () => this.exportAllData('csv'),
      } as MenuItemDef,
      'separator' as string,
      'copy' as string,
      'copyWithHeaders' as string,
    ];
    return result as any;
  };

  // FE filtering state
  selectedRoles: string[] = [];
  selectedTenants: string[] = [];
  selectedStatuses: string[] = [];
  selectedUserTypes: string[] = [];

  // Filter options (populated from loaded data)
  tenantFilterOptions: { label: string; value: string }[] = [];
  userTypeFilterOptions: { label: string; value: string }[] = [];
  statusFilterOptions: { label: string; value: string }[] = [];

  minRowHeight = 62;

  // Columns that cannot be hidden (always visible)
  lockedColumns: { [key: string]: boolean } = {
    user: true,
    email: true,
    actions: true,
  };

  // Table configuration name
  tableName = TableNames.UserManagement;

  // Column definitions for AG Grid — populated in ngOnInit with translated headers
  columnDefs: ColDef[] = [];

  private buildColumnDefs(): ColDef[] {
    return [
    {
      headerName: this.tr('user'),
      field: 'user',
      colId: 'user',
      minWidth: 200,
      sortable: true,
      resizable: true,
      filter: SearchFilterComponent,
      filterParams: {
        filterType: 'search',
        clientSide: true,
        searchFields: ['firstName', 'lastName', 'email'],
      },
      flex: 2,
      valueGetter: (params: any) => {
        const user = params.data;
        if (!user) return '';
        return `${user.firstName} ${user.lastName} | ${user.email}`;
      },
      cellRenderer: (params: any) => {
        const user = params.data;
        if (!user) return '';
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.flexDirection = 'column';
        container.style.justifyContent = 'flex-start';
        const nameDiv = document.createElement('div');
        nameDiv.className = 'fnt-w-700 fs-14';
        nameDiv.style.fontWeight = 'bold';
        nameDiv.textContent = `${user.firstName} ${user.lastName}`;
        const emailDiv = document.createElement('div');
        emailDiv.className = 'fnt-w-500 fnt-12';
        emailDiv.textContent = user.email;
        container.appendChild(nameDiv);
        container.appendChild(emailDiv);
        return container;
      },
    },
    {
      headerName: this.tr('userType'),
      field: 'userType',
      colId: 'userType',
      minWidth: 150,
      sortable: true,
      resizable: true,
      filter: GenericMultiSelectFilterComponent,
      filterParams: { options: this.userTypeFilterOptions, filterType: 'userType' },
      flex: 1.5,
      valueGetter: (params: any) => {
        const user = params.data;
        if (!user) return '';
        return this.getUserTypeLabel(user.userType);
      },
      cellRenderer: (params: any) => {
        const user = params.data;
        if (!user) return '';

        const container = document.createElement('div');
        container.style.alignItems = 'center';
        container.style.height = '100%';
        container.style.cursor = 'default';

        const label = document.createElement('span');
        label.textContent = this.getUserTypeLabel(user.userType);
        container.appendChild(label);

        return container;
      },
    },
    {
      headerName: this.tr('tenantsColumn'),
      field: 'tenants',
      colId: 'tenants',
      minWidth: 200,
      sortable: false,
      resizable: true,
      filter: GenericMultiSelectFilterComponent,
      filterParams: {
        options: this.tenantFilterOptions,
        filterType: 'tenants',
        clientSide: true,
        searchByLabelOnly: true,
      },
      flex: 3,
      autoHeight: true,
      valueGetter: (params: any) => {
        const user = params.data;
        if (!user || !user.userTenants || user.userTenants.length === 0) return '';
        return this.sortTenantsByLabel(user.userTenants)
          .map(getAdminTenantLabel)
          .join(', ');
      },
      cellRenderer: (params: any) => {
        const user = params.data;
        if (!user || !user.userTenants || user.userTenants.length === 0) return '';

        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.flexWrap = 'wrap';
        container.style.alignItems = 'flex-start';
        container.style.gap = '4px';
        container.style.rowGap = '4px';
        container.style.padding = '4px 0';

        this.sortTenantsByLabel(user.userTenants).forEach((tenant) => {
          const groupNames = this.getPermissionGroupDisplayNames(user, tenant.permissionGroupNames);
          const span = document.createElement('span');
          let text = getAdminTenantLabel(tenant);
          if (groupNames.length > 0) text += ` ${groupNames.length}`;
          span.textContent = text;

          span.style.borderRadius = '100px';
          span.style.background = '#F5F3EF';
          span.style.height = '26px';
          span.style.padding = '4px 12px';
          span.style.fontWeight = '600';
          span.style.fontSize = '13px';
          span.style.whiteSpace = 'nowrap';
          span.style.display = 'inline-flex';
          span.style.alignItems = 'center';

          if (groupNames.length > 0) {
            span.style.cursor = 'default';
            span.addEventListener('mouseenter', () => {
              this.hideTooltip();

              const tooltip = document.createElement('div');
              tooltip.style.position = 'fixed';
              tooltip.style.zIndex = '9999';
              tooltip.style.background = '#1a1a1a';
              tooltip.style.borderRadius = '8px';
              tooltip.style.padding = '12px 16px';
              tooltip.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';

              groupNames.forEach((name: string) => {
                const item = document.createElement('div');
                item.style.padding = '2px 0';
                item.style.fontSize = '14px';
                item.style.fontWeight = '500';
                item.style.color = '#fff';
                item.textContent = name;
                tooltip.appendChild(item);
              });

              document.body.appendChild(tooltip);
              this.currentTooltip = tooltip;

              const rect = span.getBoundingClientRect();
              const tooltipRect = tooltip.getBoundingClientRect();
              let left = rect.left;
              let top = rect.bottom + 4;

              if (left + tooltipRect.width > window.innerWidth) {
                left = window.innerWidth - tooltipRect.width - 8;
              }
              if (top + tooltipRect.height > window.innerHeight) {
                top = rect.top - tooltipRect.height - 4;
              }

              tooltip.style.left = left + 'px';
              tooltip.style.top = top + 'px';
            });

            span.addEventListener('mouseleave', () => {
              this.hideTooltip();
            });
          }

          container.appendChild(span);
        });

        return container;
      },
    },
    {
      headerName: this.tr('statusLabel'),
      field: 'approvalStatus',
      colId: 'status',
      hide: true,
      minWidth: 200,
      sortable: true,
      resizable: true,
      filter: GenericMultiSelectFilterComponent,
      filterParams: { options: this.statusFilterOptions, filterType: 'status' },
      flex: 1,
      valueGetter: (params: any) => this.resolveStatusLabel(params.data),
      cellRenderer: (params: any) => {
        const user = params.data;
        if (!user) return '';
        const span = document.createElement('span');
        const statusClass: Partial<Record<UserStatus, string>> = {
          [UserStatus.PendingApproval]: 'pending-status',
          [UserStatus.Approved]: 'pending-status',
          [UserStatus.Active]: 'active-status',
          [UserStatus.Rejected]: 'rejected-status',
        };
        const s = user.approvalStatus;
        span.className = (s !== undefined ? statusClass[s as UserStatus] : null)
          ?? (user.isActiveUser ? 'active-status' : 'pending-status');
        span.textContent = this.resolveStatusLabel(user);
        return span;
      },
    },
    {
      headerName: '',
      field: 'actions',
      colId: 'actions',
      valueGetter: () => '',
      minWidth: 70,
      maxWidth: 70,
      resizable: false,
      sortable: false,
      filter: false,
      suppressMovable: true,
      hide: false,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      cellRenderer: (params: any) => {
        const user = params.data;
        if (!user) return '';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn user-action position-relative';
        button.setAttribute('data-user-id', user.userId);
        button.innerHTML = '<i class="icon-dots"></i>';

        return button;
      },
    },
  ];
  }

  // Default column properties
  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    suppressHeaderMenuButton: true,
  };

  constructor(
    private router: Router,
    private service: UserManagementService,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private tenantService: TenantService,
    private authService: AuthService,
    private tokenService: TokenService,
    private fb: FormBuilder,
  ) {
    this.fetchData();
    this.search
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap((value) => { this.searchField = value; this.applyFilters(); }),
      )
      .subscribe();
    this.tenantService.getTenantNames().subscribe((data) => {
      this.tenantNames = data;
    });
  }

  get isAdminView(): boolean {
    return this.router.url.includes('/admin');
  }

  tr(key: string, ...params: any[]): string {
    return t(key, this.isAdminView, ...params);
  }

  ngOnInit(): void {
    this.columnDefs = this.buildColumnDefs();
  }

  ngAfterViewInit(): void {
    if (this.stickyToolbar) {
      this.toolbarResizeObserver = new ResizeObserver(() => {
        const h = this.stickyToolbar.nativeElement.offsetHeight;
        document.documentElement.style.setProperty('--user-mgmt-sticky-top', `${h}px`);
      });
      this.toolbarResizeObserver.observe(this.stickyToolbar.nativeElement);
    }
    this.reconnectScrollObserver();
  }

  fetchData(isUserUpdate: boolean = false): void {
    this.isLoading = true;
    this.service.getUserList(1, 999999, '')
      .subscribe((data) => {
        if (data) {
          this.allUsers = data.users;
          if (isUserUpdate && this.selectedUser?.userId) {
            const updated = data.users.find((u: UserManagementDTO) => u.userId === this.selectedUser?.userId);
            if (updated) this.selectedUser = { ...updated };
          }
          this.buildDynamicFilterOptions();
          this.applyFilters();
        }
        this.isLoading = false;
      });
  }

  /**
   * The routed content scrolls inside `.app-content` (see app.component.scss),
   * not the window, so a `window:scroll` listener never fires here. Instead we
   * watch a sentinel element at the bottom of the grid with an IntersectionObserver.
   */
  private reconnectScrollObserver(): void {
    if (!this.scrollSentinel) return;
    if (!this.scrollObserver) {
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && this.hasMoreData && !this.loadingMore) {
            this.loadMoreData();
          }
        },
        { rootMargin: '300px 0px', threshold: 0 }
      );
    }
    this.scrollObserver.unobserve(this.scrollSentinel.nativeElement);
    this.scrollObserver.observe(this.scrollSentinel.nativeElement);
  }

  loadMoreData(): void {
    if (this.loadingMore || !this.hasMoreData) return;
    this.loadingMore = true;
    const start = this.currentPage * this.pageSize;
    const next = this.filteredUsers.slice(start, start + this.pageSize);
    this.displayedUsers = [...this.displayedUsers, ...next];
    this.currentPage++;
    this.hasMoreData = this.filteredUsers.length > this.currentPage * this.pageSize;
    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.displayedUsers);
    }
    this.loadingMore = false;
    setTimeout(() => this.reconnectScrollObserver());
  }

  applyFilters(): void {
    let result = [...this.allUsers];

    if (this.searchField.trim()) {
      const q = this.searchField.trim().toLowerCase();
      result = result.filter((u) =>
        `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q)
      );
    }

    if (this.selectedTenants.length > 0) {
      result = result.filter((u) =>
        u.userTenants?.some((t: AdminTenantDto) => this.selectedTenants.includes(t.tenantName))
      );
    }

    if (this.selectedStatuses.length > 0) {
      result = result.filter((u) => {
        if (u.approvalStatus === undefined) return false;
        return this.selectedStatuses.some((sel) => sel === String(u.approvalStatus));
      });
    }

    if (this.selectedUserTypes.length > 0) {
      result = result.filter((u) => u.userType !== undefined && this.selectedUserTypes.includes(String(u.userType)));
    }

    this.filteredUsers = result;
    this.currentPage = 1;

    const hasSort = this.gridApi?.getColumnState().some(col => col.sort !== null && col.sort !== undefined) ?? false;
    if (hasSort) {
      this.displayedUsers = [...this.filteredUsers];
      this.hasMoreData = false;
    } else {
      this.displayedUsers = this.filteredUsers.slice(0, this.pageSize);
      this.hasMoreData = this.filteredUsers.length > this.pageSize;
    }

    if (this.gridApi) {
      this.gridApi.setGridOption('rowData', this.displayedUsers);
    }
    setTimeout(() => this.reconnectScrollObserver());
  }

  public onConfirm(): void {
    this.onClose.next(true);
  }

  public onCancel(): void {
    this.onClose.next(false);
  }

  openEditUser(template: TemplateRef<any>): void {
    const originalTenantIds = (this.selectedUser?.userTenants || [])
      .map(t => {
        const match = this.tenantNames.find(tn =>
          tn.name === t.displayName || tn.name === t.tenantName
        );
        return match?.id ?? t.displayName ?? t.tenantName;
      })
      .filter((id): id is string => !!id);
    this.modalForm = this.fb.group({
      id: [this.selectedUser?.userId],
      firstName: [this.selectedUser?.firstName, [Validators.required, this.trimRequired()]],
      lastName: [this.selectedUser?.lastName, [Validators.required, this.trimRequired()]],
      email: [{ value: this.selectedUser?.email, disabled: true }],
      tenantIds: [originalTenantIds as any],
    });
    this.modalRef = this.modalService.show(template, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: true,
    });
    const req = this.onClose.subscribe((result: boolean) => {
      if (result) {
        const raw = this.modalForm.getRawValue();
        const newTenantIds: string[] = raw.tenantIds ?? [];
        const payload = {
          id: raw.id,
          firstName: raw.firstName,
          lastName: raw.lastName,
          tenantsToAdd: newTenantIds.filter(id => !originalTenantIds.includes(id)),
          tenantsToRemove: originalTenantIds.filter(id => !newTenantIds.includes(id)),
        };
        this.service.updateUserDataByAdmin(payload)
          .subscribe(() => {
            this.fetchData(false);
            this.modalRef?.hide();
            req.unsubscribe();
          });
      } else {
        req.unsubscribe();
        this.modalRef?.hide();
        this.fetchData(false);
      }
    });
  }

  resetPassword(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, {
      class: 'modal-md modal-dialog-centered',
    });
    const req = this.onClose.subscribe((result: boolean) => {
      if (result) {
        this.authService.ResetPasswordByAdmin(this.selectedUser?.userId).subscribe();
        req.unsubscribe();
        this.modalRef?.hide();
      } else {
        req.unsubscribe();
        this.modalRef?.hide();
      }
    });
  }

  openDeleteUser(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, {
      class: 'modal-md modal-dialog-centered',
    });
    const req = this.onClose.subscribe((result: boolean) => {
      if (result) {
        this.service.deleteUser(this.selectedUser?.userId).subscribe(() => this.fetchData(false));
        req.unsubscribe();
        this.modalRef?.hide();
        this.toastr.success(this.tr('userRemovedSuccess'));
        this.allUsers = this.allUsers.filter((u) => u.userId !== this.selectedUser?.userId);
        this.applyFilters();
      } else {
        req.unsubscribe();
        this.modalRef?.hide();
      }
    });
  }

  createUser(template: TemplateRef<any>) {
    this.isEmailExist = false;
    this.isEmailChecking = false;
    this.createUserForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.maxLength(100), this.trimRequired()]],
      lastName: ['', [Validators.required, Validators.maxLength(100), this.trimRequired()]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(100)]],
      tenantName: ['', Validators.required],
    });
    this.createUserForm.get('email')?.valueChanges
      .pipe(
        filter((email: string) => !!email && !!this.createUserForm.get('email')?.valid),
        tap(() => { this.isEmailExist = false; this.isEmailChecking = true; }),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((email: string) => this.authService.CheckEmail(email)),
        map((res: { isExist: boolean }) => res.isExist),
      )
      .subscribe((value: boolean) => {
        this.isEmailExist = value;
        this.isEmailChecking = false;
      });
    this.modalRef = this.modalService.show(template, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: true,
    });
  }

  get cf() {
    return this.createUserForm.controls;
  }

  cancelCreate(): void {
    this.modalRef?.hide();
  }

  saveCreateUser(): void {
    if (this.createUserForm.invalid || this.isEmailExist || this.isEmailChecking) return;
    const { firstName, lastName, email, tenantName: tenantId } = this.createUserForm.value;
    const tenant = this.tenantNames.find(t => t.id === tenantId);
    if (!tenant) return;
    this.isCreatingUser = true;
    this.service.createUser({ email, firstName, lastName, tenantId } as any)
      .subscribe({
        next: () => {
          this.isCreatingUser = false;
          this.modalRef?.hide();
          this.toastr.success(this.tr('userAddedSuccess'));
          this.fetchData(false);
        },
        error: () => {
          this.isCreatingUser = false;
          this.toastr.error(this.tr('userCouldNotBeAdded'));
        },
      });
  }

  private trimRequired() {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      if (!value.trim()) {
        return { required: true };
      }
      return null;
    };
  }

  onRowItemClicked(event: any): void {
    const target = event.event?.target as HTMLElement | undefined;
    if (target?.closest('button.user-action')) return;
    if (!event.data) return;

    this.selectedUser = event.data;
    this.openEditUser(this.editUserTemplate);
  }

  // AG Grid event handlers
  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;

    if (this.displayedUsers.length) {
      this.gridApi.setGridOption('rowData', this.displayedUsers);
    }

    // Update filter options if they're already loaded
    this.updateFilterOptions();

    // Setup dropdown menu listener
    setTimeout(() => {
      this.setupDropdownListener();
    }, 100);
  }

  private currentDropdown: HTMLElement | null = null;

  private setupDropdownListener(): void {
    // Remove existing listener if any
    if (this.dropdownListener) {
      document.removeEventListener('click', this.dropdownListener);
    }

    this.dropdownListener = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const button = target.closest('button.user-action');

      // Handle dropdown button click
      if (button) {
        event.stopPropagation();
        const userId = button.getAttribute('data-user-id');

        if (userId) {
          const user = this.allUsers.find((u) => u.userId === userId);
          if (user) {
            this.selectedUser = user;
            this.showDropdownMenu(button as HTMLElement, user.userId);
          }
        }
        return;
      }

      // Handle menu item click
      if (target.classList.contains('action-button-item')) {
        const action = target.getAttribute('data-action');

        this.hideDropdownMenu();

        // Execute action in Angular zone to ensure change detection
        if (action === 'reset') {
          setTimeout(() => this.resetPassword(this.templateResetPassword), 0);
        } else if (action === 'edit') {
          setTimeout(() => this.openEditUser(this.editUserTemplate), 0);
        } else if (action === 'delete') {
          setTimeout(() => this.openDeleteUser(this.templateDeleteUser), 0);
        }
        return;
      }

      // Click outside - close dropdown
      if (this.currentDropdown && !target.closest('.user-dropdown-menu')) {
        this.hideDropdownMenu();
      }
    };

    document.addEventListener('click', this.dropdownListener);
  }

  private showDropdownMenu(button: HTMLElement, _userId: string): void {
    // Hide any existing dropdown
    this.hideDropdownMenu();

    const isOwnAccount = this.selectedUser.userId === this.tokenService.getUserId();

    // Create dropdown menu
    const menu = document.createElement('div');
    menu.className = 'user-dropdown-menu';

    menu.innerHTML = `
      <ul style="list-style: none; padding: 0; margin: 0;">
        <li class="action-button-item" data-action="reset">${this.tr('resetPassword')}</li>
        <li class="action-button-item" data-action="edit">${this.tr('editUser')}</li>
        ${!isOwnAccount ? `<li class="action-button-item" data-action="delete">${this.tr('deleteUser')}</li>` : ''}
      </ul>
    `;

    // Append to body first
    document.body.appendChild(menu);

    // Position the menu after it's in the DOM
    const rect = button.getBoundingClientRect();
    const menuWidth = menu.offsetWidth || 150;
    menu.style.left = rect.left - menuWidth + rect.width + 'px';
    menu.style.top = rect.bottom + 5 + 'px';

    this.currentDropdown = menu;
  }

  private hideDropdownMenu(): void {
    if (this.currentDropdown) {
      this.currentDropdown.remove();
      this.currentDropdown = null;
    }
  }

  private buildDynamicFilterOptions(): void {
    // User type — only types present in loaded data
    const presentTypes = [...new Set(this.allUsers.map((u) => u.userType).filter((t): t is UserType => t !== undefined))];
    this.userTypeFilterOptions = presentTypes
      .sort((a, b) => a - b)
      .map((type) => ({ label: this.getUserTypeLabel(type), value: String(type) }));

    // Tenants — only tenants present in loaded data
    const tenantLabels = new Map<string, string>();
    this.allUsers.forEach((u) =>
      u.userTenants?.forEach((t) => {
        if (t.tenantName) tenantLabels.set(t.tenantName, getAdminTenantLabel(t));
      })
    );
    this.tenantFilterOptions = buildTenantFilterOptions(tenantLabels.keys(), tenantLabelLookupFromMap(tenantLabels));

    const presentStatuses = new Set(
      this.allUsers.map((u) => u.approvalStatus).filter((s): s is UserStatus => s !== undefined)
    );
    this.statusFilterOptions = [];
    if (presentStatuses.has(UserStatus.PendingApproval)) {
      this.statusFilterOptions.push({ label: this.tr('pendingApproval'), value: String(UserStatus.PendingApproval) });
    }
    if (presentStatuses.has(UserStatus.Approved)) {
      this.statusFilterOptions.push({ label: this.tr('pending'), value: String(UserStatus.Approved) });
    }
    if (presentStatuses.has(UserStatus.Active)) {
      this.statusFilterOptions.push({ label: this.tr('activeLabel'), value: String(UserStatus.Active) });
    }
    if (presentStatuses.has(UserStatus.Rejected)) {
      this.statusFilterOptions.push({ label: this.tr('rejected'), value: String(UserStatus.Rejected) });
    }

    this.updateFilterOptions();
  }

  private updateFilterOptions(): void {
    if (!this.gridApi) return;

    const userTypeColDef = this.columnDefs.find((col) => col.colId === 'userType');
    if (userTypeColDef?.filterParams) {
      userTypeColDef.filterParams['options'] = this.userTypeFilterOptions;
    }

    const tenantsColDef = this.columnDefs.find((col) => col.colId === 'tenants');
    if (tenantsColDef?.filterParams) {
      tenantsColDef.filterParams['options'] = this.tenantFilterOptions;
    }

    const statusColDef = this.columnDefs.find((col) => col.colId === 'status');
    if (statusColDef?.filterParams) {
      statusColDef.filterParams['options'] = this.statusFilterOptions;
    }

    this.agGridTable?.refreshColumnDefs(this.columnDefs);
  }

  onSortChanged(): void {
    this.applyFilters();
  }

  onFilterChanged(): void {
    if (!this.gridApi) return;

    const filterModel = this.gridApi.getFilterModel();

    this.searchField = filterModel['user']?.searchText || '';
    this.selectedRoles = [];
    this.selectedTenants = filterModel['tenants']?.conditions || [];
    this.selectedStatuses = filterModel['status']?.conditions || [];
    this.selectedUserTypes = filterModel['userType']?.conditions || [];

    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    if (this.searchField && this.searchField.trim() !== '') return true;
    if (this.selectedTenants && this.selectedTenants.length > 0) return true;
    if (this.selectedStatuses.length > 0) return true;
    if (this.selectedUserTypes.length > 0) return true;
    if (this.gridApi) {
      if (Object.keys(this.gridApi.getFilterModel()).length > 0) return true;
      if (this.gridApi.getColumnState().some(col => col.sort !== null && col.sort !== undefined)) return true;
    }
    return false;
  }

  clearFilters(): void {
    this.searchField = '';
    this.search.next('');
    this.selectedRoles = [];
    this.selectedTenants = [];
    this.selectedStatuses = [];
    this.selectedUserTypes = [];

    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
      this.gridApi.applyColumnState({ defaultState: { sort: null } });
    }

    this.applyFilters();
  }

  private exportAllData(format: 'excel' | 'csv'): void {
    if (!this.gridApi) return;

    const currentData = this.displayedUsers;
    this.gridApi.setGridOption('rowData', this.allUsers);

    setTimeout(() => {
      const fileName = `User_Management_${new Date().toISOString().split('T')[0]}`;
      const visibleColumnKeys = this.gridApi
        .getColumnDefs()
        ?.filter((col: any) => col.field && col.field !== 'actions' && !col.hide)
        .map((col: any) => col.field) || [];

      const exportParams = {
        allColumns: false,
        columnKeys: visibleColumnKeys,
        fileName,
        processCellForClipboard: (params: any) => {
          const colDef = this.gridApi
            .getColumnDefs()
            ?.find((col: any) => col.field === params.column?.getColId()) as any;
          if (colDef?.cellRenderer && typeof colDef.cellRenderer === 'function') {
            const rendered = colDef.cellRenderer({ value: params.value, data: params.node?.data });
            if (typeof rendered === 'string') return rendered.replace(/<[^>]*>/g, '');
            return rendered || '';
          }
          if (colDef?.valueGetter && typeof colDef.valueGetter === 'function') {
            return colDef.valueGetter({ data: params.node?.data }) || '';
          }
          return params.value !== null ? String(params.value) : '';
        },
      };

      if (format === 'excel') {
        this.gridApi.exportDataAsExcel(exportParams);
      } else {
        this.gridApi.exportDataAsCsv(exportParams);
      }

      this.gridApi.setGridOption('rowData', currentData);
    }, 100);
  }

  getUserStatusLabel(status?: UserStatus): string {
    if (status === undefined || status === null) return '';
    if (status === UserStatus.PendingApproval) return this.tr('pendingApproval');
    if (status === UserStatus.Approved) return this.tr('pending');
    if (status === UserStatus.Active) return this.tr('activeLabel');
    if (status === UserStatus.Rejected) return this.tr('rejected');
    return `Status ${status}`;
  }

  resolveStatusLabel(user?: UserManagementDTO): string {
    if (!user) return '';
    if (user.approvalStatus !== undefined) return this.getUserStatusLabel(user.approvalStatus);
    return user.isActiveUser ? 'Active' : 'Pending Approval';
  }

  getFeatureNames(features?: number[]): string[] {
    if (!features?.length) return [];
    return features
      .map((id) => FEATURE_ROLES.find((f) => f.featureId === id)?.featureName)
      .filter((name): name is string => !!name);
  }

  private get isCurrentUserGlobalAdmin(): boolean {
    try {
      const token = this.tokenService.getToken();
      if (!token) return false;
      const decoded = jwt_decode<Record<string, string | string[]>>(token);
      const roles = decoded[environment.roleKey];
      return Array.isArray(roles) ? roles.includes(ADMIN_ROLE) : roles === ADMIN_ROLE;
    } catch {
      return false;
    }
  }

  private sortTenantsByLabel(tenants: AdminTenantDto[]): AdminTenantDto[] {
    return [...tenants].sort((a, b) => getAdminTenantLabel(a).localeCompare(getAdminTenantLabel(b)));
  }

  getPermissionGroupDisplayNames(_user: UserManagementDTO, names?: string[]): string[] {
    if (!names?.length) return [];
    if (this.isCurrentUserGlobalAdmin) return names;
    return names.map((name) => {
      if (name === FULL_ACCESS_ENG) return FULL_ACCESS_GER;
      if (name === NO_PERMISSION_ENG) return NO_PERMISSION_GER;
      return name;
    });
  }

  getUserTypeLabel(userType?: UserType): string {
    const typeMap: { [key in UserType]: string } = {
      [UserType.PartnerAdmin]: this.tr('partnerAdmin'),
      [UserType.TenantAdmin]: this.tr('tenantAdmin'),
      [UserType.PartnerUser]: this.tr('partnerUser'),
      [UserType.TenantUser]: this.tr('tenantUser'),
      [UserType.SuperUser]: this.tr('globalUser'),
    };
    if (userType === undefined || userType === null) return '';
    return typeMap[userType] ?? `Type ${userType}`;
  }

  private hideTooltip(): void {
    if (this.currentTooltip) {
      this.currentTooltip.remove();
      this.currentTooltip = null;
    }
  }

  ngOnDestroy(): void {
    this.toolbarResizeObserver?.disconnect();
    this.scrollObserver?.disconnect();
    if (this.dropdownListener) {
      document.removeEventListener('click', this.dropdownListener);
    }
    this.hideDropdownMenu();
    this.hideTooltip();
  }
}
